import React from "react";
import { useT } from "@/i18n/LanguageProvider";
import Reveal from "./Reveal";
import Chevron from "./Chevron";
import PhoneMockup from "./PhoneMockup";

/**
 * AppDownload — Branded store-badge section
 * Shows App Store + Google Play download buttons with a phone-screen accent.
 * Placeholder links (#) until real store URLs are provided.
 */

function AppleBadge({ subtitle, title }) {
  return (
    <a
      href="#"
      aria-label="Download on the App Store"
      data-testid="cta-app-store"
      className="group flex items-center gap-3 pl-4 pr-5 py-3 rounded-2xl bg-black text-white border border-white/10 hover:border-delfi-red/60 hover:-translate-y-0.5 transition-all duration-300 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.5)]"
    >
      <svg viewBox="0 0 384 512" className="w-7 h-7 fill-current" aria-hidden="true">
        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM236.5 105.3c20.1-24.3 31-50.9 28.4-79.3-25.6 1.6-56.8 17.5-74.6 38.8-15.6 18.4-29.8 47.2-26.5 73.7 28.6 2.4 56.5-13.4 72.7-33.2z"/>
      </svg>
      <span className="flex flex-col leading-tight">
        <span className="text-[10px] tracking-wide opacity-80 uppercase">{subtitle}</span>
        <span className="text-base font-semibold tracking-tight">{title}</span>
      </span>
    </a>
  );
}

function GoogleBadge({ subtitle, title }) {
  return (
    <a
      href="#"
      aria-label="Get it on Google Play"
      data-testid="cta-google-play"
      className="group flex items-center gap-3 pl-4 pr-5 py-3 rounded-2xl bg-black text-white border border-white/10 hover:border-delfi-red/60 hover:-translate-y-0.5 transition-all duration-300 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.5)]"
    >
      <svg viewBox="0 0 512 512" className="w-7 h-7" aria-hidden="true">
        <path d="M325.3 234.3 104.6 13l280.8 161.2-60.1 60.1z" fill="#EA4335" />
        <path d="M104.6 499 325.3 277.7l-60.1-60.1L104.6 13z" fill="#FBBC04" />
        <path d="M104.6 13v486l116.2-243z" fill="#34A853" />
        <path d="m385.4 337-60.1-59.3L265.2 217.6 104.6 499z" fill="#4285F4" />
      </svg>
      <span className="flex flex-col leading-tight">
        <span className="text-[10px] tracking-wide opacity-80 uppercase">{subtitle}</span>
        <span className="text-base font-semibold tracking-tight">{title}</span>
      </span>
    </a>
  );
}

export default function AppDownload() {
  const t = useT();
  const d = t.appDownload;

  return (
    <section
      className="relative bg-delfi-navy text-white overflow-hidden py-24 md:py-28"
      data-testid="home-app-download-section"
    >
      {/* Soft red glow accents */}
      <div className="hero-glow left-[-220px] top-[-180px]" aria-hidden="true" />
      <div className="hero-glow right-[-260px] bottom-[-220px] opacity-60" aria-hidden="true" />
      <div className="chevron-bg right-[-12%] bottom-[-30%] opacity-[0.06]" aria-hidden="true">›</div>

      <div className="delfi-container relative grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
        <div>
          <Reveal>
            <div className="inline-flex items-center gap-2 text-delfi-gold text-xs tracking-[0.2em] uppercase font-semibold">
              <Chevron size={14} color="#D4AF37" /> {d.eyebrow}
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h2
              className="headline-display text-white mt-5 text-[clamp(1.9rem,3.6vw,2.9rem)] leading-tight"
              data-testid="app-download-headline"
            >
              {d.headlineLead}{" "}
              <span className="text-delfi-gold">{d.headlineHighlight}</span>
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-6 text-white/75 max-w-xl leading-relaxed">{d.body}</p>
          </Reveal>

          <Reveal delay={220}>
            <div className="mt-9 flex flex-wrap gap-4" data-testid="app-download-badges">
              <AppleBadge subtitle={d.appleSubtitle} title={d.appleTitle} />
              <GoogleBadge subtitle={d.googleSubtitle} title={d.googleTitle} />
            </div>
          </Reveal>

          <Reveal delay={300}>
            <ul className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-white/70 max-w-lg">
              {(d.bullets || []).map((b, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-delfi-red shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        {/* Real Delfi app home-screen preview */}
        <Reveal delay={180}>
          <div className="relative hidden lg:flex items-center justify-center" aria-hidden="true">
            <PhoneMockup />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
